import { useMutation, useQueryClient } from '@tanstack/react-query';

import { useNavigate } from '@change-corgi/core/react/router';
import { useTracking } from '@change-corgi/core/react/tracking';
import { localStorage } from '@change-corgi/core/storage';

import { useHomepagePageUserData } from 'src/app/pages/homepage/pageContext';
import {
	DraftClient,
	findOrCreateDraftAuthorId,
	InMemoryAdapter,
	LocalStorageAdapter,
} from 'src/app/shared/api/petitionDraft';

type ExperimentsData = ReturnType<typeof useHomepagePageUserData>['data']['experiments'];

/**
 * Determines the draft type based on the starfishSap3RecommendedAngles experiment variant.
 * - 'variant': returns 'recommendations_draft' to enable the recommendations flow
 * - 'control' or not enrolled: returns 'ai_draft' for the default AI draft flow
 */
function getDraftTypeFromExperiment(experiments: ExperimentsData) {
	const { variation } = experiments.starfishSap3RecommendedAngles;
	return variation.includes('control') ? ('ai_draft' as const) : ('recommendations_draft' as const);
}

export function useCreateDraft() {
	const navigate = useNavigate();
	const track = useTracking();
	const queryClient = useQueryClient();
	const draftAuthorId = findOrCreateDraftAuthorId({ storage: localStorage, queryClient });
	const {
		data: { experiments },
	} = useHomepagePageUserData();

	const draftType = getDraftTypeFromExperiment(experiments);

	const draftClient = new DraftClient([new LocalStorageAdapter(localStorage), new InMemoryAdapter(queryClient)]);

	return useMutation({
		mutationFn: async (draftInput: string) => {
			const draft = { input: { issuePrompt: draftInput }, type: draftType };
			return draftClient.createDraft(draftAuthorId, draft);
		},
		onSuccess: () => {
			navigate('/start-a-petition/personal-story?started_flow=false&source_location=homepage_primary_button');
		},
		onError: () => {
			void track('homepage_sap_draft_submission_error');
		},
	});
}
